import React from 'react';
import { Box } from '@mui/material';
import { BookmarkToggle } from './BookmarkToggle';

interface FieldRowProps {
  /** Whether the field is bookmarked */
  addToActionItems?: boolean;
  /** Callback when bookmark is toggled */
  onBookmarkToggle: () => void;
  /** The field content to render */
  children: React.ReactNode;
  /** Additional spacing/styling options */
  sx?: any;
  /** Whether to align items at start (for checkboxes) */
  alignStart?: boolean;
  /** Optional icon/button rendered between children and the bookmark, aligned identically to the bookmark */
  trailingAction?: React.ReactNode;
  /** Whether to show the bookmark toggle (defaults to true) */
  showBookmark?: boolean;
}

/**
 * Wrapper component that adds a bookmark toggle to any field
 * Provides consistent spacing and alignment across all field types
 */
export const FieldRow: React.FC<FieldRowProps> = ({
  addToActionItems,
  onBookmarkToggle,
  children,
  sx,
  alignStart = false,
  trailingAction,
  showBookmark = true,
}) => {
  const trailingSx = alignStart ? { pt: 0.75 } : undefined;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: alignStart ? 'flex-start' : 'center',
        gap: 1,
        width: '100%',
        ...sx,
      }}
    >
      <Box sx={{ flexGrow: 1, width: '100%' }}>{children}</Box>
      {trailingAction && <Box sx={{ flexShrink: 0, ...trailingSx }}>{trailingAction}</Box>}
      {showBookmark ? (
        <BookmarkToggle addToActionItems={addToActionItems} onToggle={onBookmarkToggle} sx={trailingSx} />
      ) : (
        <Box sx={{ width: 24, flexShrink: 0, ...trailingSx }} />
      )}
    </Box>
  );
};
